import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useTable, CellProps, Column, Cell, HeaderGroup, ColumnInstance } from 'react-table'
import PopupState, { bindTrigger, bindPopover } from 'material-ui-popup-state'

import Box from '@mui/material/Box'
import Checkbox from '@mui/material/Checkbox'
import CircularProgress from '@mui/material/CircularProgress'
import IconButton from '@mui/material/IconButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import MenuItem from '@mui/material/MenuItem'
import Popover from '@mui/material/Popover'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell, { TableCellProps } from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import InfoIcon from '@mui/icons-material/Info'
import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1'
import SettingsIcon from '@mui/icons-material/Settings'

import DelayedRender from '@/components/hoc/DelayedRender'

import colors from '@/assets/scss/_themes-vars.module.scss'

export type StaffData = {
  id: string
  cps?: string
  fullName: string
  email: string
  hasOrdoclicAccount?: boolean
  status: string
}

export type StaffsTableProps = {
  data: StaffData[]
  loading?: boolean
  onCreateOrdoclicAccount?: (staffId: string) => void
}

const rowHeight = 60

export function StaffsTable(props: StaffsTableProps) {
  const { data, loading, onCreateOrdoclicAccount } = props
  const { t } = useTranslation()

  const columns = useMemo(() => {
    return [
      {
        Header: t('page.superAdmin.staffsTable.header.fullName'),
        accessor: 'fullName',
        Cell: ({ value }: CellProps<StaffData, string>) => (
          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '15px' }}>
            {value.toUpperCase()}
          </Typography>
        ),
      },
      {
        Header: t('page.superAdmin.staffsTable.header.email'),
        accessor: 'email',
      },
      {
        Header: t('page.superAdmin.staffsTable.header.cps'),
        accessor: 'cps',
      },
      {
        Header: t('page.superAdmin.staffsTable.header.status'),
        accessor: 'status',
        align: 'center',
      },
      {
        Header: t('page.superAdmin.staffsTable.header.ordoclicAccount'),
        accessor: 'hasOrdoclicAccount',
        Cell: ({ value }: CellProps<StaffData, boolean>) => (
          <Checkbox disabled checked={value} size="small" sx={{ '&.Mui-disabled': { color: (theme) => theme.palette.primary.main } }} />
        ),
        align: 'center',
      },
    ] as Column<StaffData>[]
  }, [t])

  const { getTableBodyProps, headerGroups, prepareRow, rows } = useTable<StaffData>({
    columns,
    data,
  })

  return (
    <Table stickyHeader>
      <TableHead sx={{ bgcolor: colors.gray100 }}>
        {headerGroups.map((headerGroup: HeaderGroup<StaffData>) => (
          <TableRow {...headerGroup.getHeaderGroupProps()}>
            {headerGroup.headers.map((column: HeaderGroup<StaffData>) => {
              return (
                <TableCell {...column.getHeaderProps()} align={column.align} scope="col">
                  {column.render('Header')}
                </TableCell>
              )
            })}
            <TableCell
              align="center"
              sx={{ width: '50px', position: 'sticky', right: 0, borderLeft: '1px solid #efefef', zIndex: 10, background: '#f9fafb', minWidth: '50px' }}
            >
              <Stack alignItems="center" justifyContent="center">
                <SettingsIcon />
              </Stack>
            </TableCell>
          </TableRow>
        ))}
      </TableHead>
      <TableBody {...getTableBodyProps()} sx={{ position: 'relative' }}>
        {loading && (
          <DelayedRender delay={300}>
            <TableRow>
              <TableCell sx={{ border: 'none', padding: 0 }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    background: 'rgba(255, 255, 255, 0.7)',
                  }}
                >
                  <CircularProgress size={20} />
                </Box>
              </TableCell>
            </TableRow>
          </DelayedRender>
        )}
        {rows.map((row) => {
          prepareRow(row)
          return (
            <TableRow {...row.getRowProps()}>
              {row.cells.map((cell: Cell<StaffData>) => (
                <TableCell
                  {...cell.getCellProps()}
                  align={(cell.column as ColumnInstance<StaffData> & { align?: TableCellProps['align'] }).align}
                  sx={{ height: rowHeight + 'px', verticalAlign: 'middle' }}
                >
                  {cell.render('Cell')}
                </TableCell>
              ))}
              <TableCell
                align="center"
                sx={{ width: '50px', position: 'sticky', right: 0, borderLeft: '1px solid #efefef', zIndex: 1, background: '#FFF', minWidth: '50px' }}
              >
                <Stack justifyContent="flex-end" direction="row" spacing={1}>
                  <PopupState variant="popover" popupId={`staffs-${row.id}-actions`}>
                    {(popupState) => (
                      <>
                        <IconButton aria-label="staff actions" {...bindTrigger(popupState)}>
                          <MoreHorizIcon />
                        </IconButton>
                        <Popover
                          {...bindPopover(popupState)}
                          anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                          slotProps={{
                            paper: {
                              sx: {
                                borderRadius: '10px',
                                p: '4px',
                                '& .MuiMenuItem-root': {
                                  px: 1,
                                  typography: 'body2',
                                  borderRadius: '6px',
                                },
                              },
                            },
                          }}
                        >
                          {!row.original.hasOrdoclicAccount && (
                            <Stack direction="row" alignItems="center">
                              <MenuItem
                                disabled={!row.original.cps}
                                onClick={() => {
                                  onCreateOrdoclicAccount?.(row.original.id)
                                  popupState.close()
                                }}
                              >
                                <ListItemIcon>
                                  <PersonAddAlt1Icon />
                                </ListItemIcon>
                                Create ordoclic account
                              </MenuItem>
                              {!row.original.cps && (
                                <Tooltip title={t('page.superAdmin.staffsTable.actions.createOrdoclicAccountDisabled.tooltip')} placement="top">
                                  <InfoIcon fontSize="small" sx={{ mr: 1 }} />
                                </Tooltip>
                              )}
                            </Stack>
                          )}
                        </Popover>
                      </>
                    )}
                  </PopupState>
                </Stack>
              </TableCell>
            </TableRow>
          )
        })}
      </TableBody>
    </Table>
  )
}

export default StaffsTable
